import { FileText } from "lucide-react"
import { useMemo, useState } from "react"

import { ReportDocumentSelection } from "@/features/report/ReportDocumentSelection"
import { SelectedDocumentPanel } from "@/features/report/SelectedDocumentPanel"
import { Badge } from "@ui/badge"

import { DynamicColumn } from "@/models/datatable"
import { FilterProperty } from "@/models/facets"
import { ReportSearchField } from "@/models/report"

import { CollectionItem, ContentObjectItem } from "@vertesia/common"

interface DocumentsSelectionPanelProps {
    configuration: CollectionItem
    documents: ContentObjectItem[]
    handleDocumentSelection: (documents: ContentObjectItem[]) => void
    handleDocumentsRemoved: (documents: ContentObjectItem[]) => void
}
export function DocumentsSelectionPanel({ configuration, documents, handleDocumentSelection, handleDocumentsRemoved }: Readonly<DocumentsSelectionPanelProps>) {
    const [typeFilter, setTypeFilter] = useState<string | undefined>(undefined)

    const handleRemoveFromSelection = (doc: ContentObjectItem) => {
        handleDocumentsRemoved(documents.filter(d => d.id !== doc.id))
    }

    const handleTypeFilterSelection = (typeId: string) => {
        setTypeFilter(typeFilter === typeId ? undefined : typeId)
    }

    const filteredDocuments = useMemo(() => {
        if (!typeFilter) {
            return documents
        }
        return documents.filter(doc => doc.type?.id === typeFilter)
    }, [documents, typeFilter])

    const documentsTypes = useMemo(() => {
        if (!documents?.length) {
            return []
        }

        const types: any[] = documents.map((doc) => doc.type).filter((type) => type !== null && type !== undefined)
        return Object.values(
            types.reduce((acc, item) => {
                if (acc[item.id]) {
                    acc[item.id].count++
                } else {
                    acc[item.id] = { ...item, count: 1 }
                }
                return acc
            }, {})
        ).sort((a: any, b: any) => a.name.localeCompare(b.name))
    }, [documents])

    return (
        <>
            {
                (documents.length === 0) ? (
                    <div className="flex flex-col items-center justify-center h-full gap-4">
                        <FileText className="h-16 w-16 text-gray-300" />
                        <div className="text-center">
                            <h3 className="text-lg font-medium text-gray-900">
                                No documents selected
                            </h3>
                            <p className="text-gray-600">
                                Search to find and select documents for your report
                            </p>
                        </div>
                        <ReportDocumentSelection configuration={configuration} handleDocumentSelection={handleDocumentSelection} />
                    </div>
                ) : (
                    <div className="flex flex-col h-full min-h-0 gap-2">
                        <div className="flex flex-wrap gap-2">
                            {
                                documentsTypes.map((type: any) => (
                                    <Badge key={type.id} onClick={() => handleTypeFilterSelection(type.id)} className={`hover:cursor-pointer ${typeFilter === type.id && "bg-blue-500 text-white hover:bg-blue-500/80"}`}>
                                        {type.name} ({type.count})
                                    </Badge>
                                ))
                            }
                        </div>
                        <div className="flex-1 min-h-0 overflow-y-auto">
                            <ul className="space-y-2">
                                {
                                    filteredDocuments.map((doc) => (
                                        <SelectedDocumentPanel document={doc} onRemoveFromSelection={handleRemoveFromSelection} />
                                    ))
                                }
                            </ul>
                        </div>
                    </div>
                )
            }
        </>
    )
}


function retrieveTypes(configuration: CollectionItem): string[] {
    const properties = (configuration as any)?.properties
    if (!properties || !properties.types) {
        return []
    }

    return properties.types?.filter((type: any) => type.id !== "all").map((type: any) => type.id) || []
}

function retrieveFilters(configuration: CollectionItem): FilterProperty[] {
    const properties = (configuration as any)?.properties
    if (!properties || !properties.filters) {
        return []
    }
    return properties.filters
}

function retrieveSearches(configuration: CollectionItem): ReportSearchField[] {
    const properties = (configuration as any)?.properties
    if (!properties || !properties.search) {
        return []
    }
    return properties.search
}

function retrieveColumns(configuration: CollectionItem): DynamicColumn[] {
    const properties = (configuration as any)?.properties
    if (!properties || !properties.columns) {
        return []
    }
    return properties.columns
}